import { type LDUser } from 'launchdarkly-js-client-sdk';
import { type DateTime } from 'luxon';
import { type Line } from 'd3';
import { type Component } from 'vue';
import { type AxiosRequestConfig } from 'axios';
import {
  type ILatestBloodPressureMeasurementValue,
  type IPatientBMIMeasurement,
  type IPatientWidgetMetricZone,
  type IBloodPressureTimeInZoneChartData,
  type IRPMAdherence,
  type IRPMAlert,
  type IRPMAlertAcknowledgement,
  type IRPMEngagement,
  type IRPMPatientBloodGlucose,
  type ITimeInZoneChartData,
  INotesWidgetNote,
  IWeightChangePoundsRangeTooltip,
  AlertFirstLevelAttributeEnum,
} from './patients.types';
import {
  IDropdownOption,
  ITimelineEvent,
  type IBloodPressureTimeSeriesData,
  type IRpmBloodGlucoseInformation,
  type IRpmBloodPressureDisplayInformation,
  type IRpmBloodPressureMeasurement,
  type IRpmContinuousGlucoseMeasurement,
  type IRpmGlucoseThresholds,
  type IRpmInsulinActing,
  type ITimeline,
} from './rpm-patient-information.types';
import { AuditCenterSearchByEnum } from './audit-center-logs.types';
import {
  type IBillingInsightsPatientAssignment,
  type IBillingInsightsPatientBluetoothMeasurement,
} from './billing-insights.types';
import {
  type DetailTabChartsEnum,
  type ISelectorView,
  type ISubSelector,
} from './views-types';
import { Media } from '@twilio/conversations';
import { IMediaOptions } from './user-types';

export enum IconTypes {
  PATIENTS = 'patients',
  PROVIDERS = 'providers',
  CLINICS = 'clinics',
  CARE_COMPOSER = 'care-composer',
  HEALTH = 'health',
}

export enum FilterTypeEnum {
  VALUE = 'value',
  RANGE = 'range',
  OPERATOR = 'operator',
  GROUP_OPERATOR = 'groupOperator',
  NOT_OPERATOR = 'notOperator',
}

export enum EducationalTaskTypeEnum {
  WEB = 'WEB',
  NEW_CONTENT = 'NEW_CONTENT',
  PDF = 'PDF',
}

export enum BloodGlucoseChartTypeEnum {
  BGM,
  CGM,
}

export enum RPMCohortViewColorsEnum {
  GRAY = 'GRAY',
  GREEN = 'GREEN',
  YELLOW = 'YELLOW',
  ORANGE = 'ORANGE',
  RED = 'RED',
  DARK_RED = 'DARK_RED',
  PURPLE = 'PURPLE',
}

export interface IIntersectionObserverOptions {
  root: HTMLElement | null;
  rootMargin?: string;
  threshold: number;
}

export enum SortingCriterionOrderEnum {
  ASC = 'asc',
  DESC = 'desc',
}

export enum GenderEnum {
  MALE = 'male',
  FEMALE = 'female',
}

export interface IWeekDay {
  index: number;
  name: string;
  checked: boolean;
}

export interface ICarePlansGenericType {
  carePlans: ICarePlan[];
}

export interface ICarePlanGenericType {
  carePlan: ICarePlan;
}

export interface ICarePlanSummary {
  education?: string[];
  lifestyle?: string[];
  links?: string[];
  medications?: string[];
  measurements?: string[];
  name: string;
  remoteId: string;
  symptoms?: string[];
  surveys?: string[];
  updatedDate: string;
  deletedDate: string;
}

export interface ICarePlanSummaryGenericType {
  carePlans: ICarePlanSummary[];
}

export interface ITasksGenericType {
  tasks: ITask[];
}

export interface ITaskGenericType {
  task: ITask;
}

export interface ILibrary {
  education: ILibraryDetails[];
}

export interface ISurveyLibrary {
  surveys: ISurveyLibraryDetails[];
  surveyTypes: string[];
}

export interface IMedicationLibrary {
  medication: IMedicationLibraryDetails[];
}

export interface IMedicationLibraryDetails {
  condition: string;
  dateCreated: string;
  dateUpdated: string;
  drugClass: string;
  ingredientName: string;
  instructions: string;
  method: string;
  strengthNumber: number;
  strengthUnit: string;
  title: string;
  remoteId: string;
}

export interface ILibraryDetails {
  articleNumber: number;
  conditions: string[];
  dateCreated: string;
  dateUpdated: string;
  description: string;
  moduleName: string;
  moduleNumber: number;
  title: string;
  url: string;
  remoteId: string;
}

export interface ISurveyLibraryDetails {
  dateCreated: string;
  dateUpdated: string;
  title: string;
  url: string;
  type: string;
  remoteId: string;
}

export type UINormalizedLibraryItem = {
  id: string;
  title: string;
  instructions?: string;
  updatedDate: string;
  kind: 'education' | 'medication' | 'survey';
  singleGroupLevel?: boolean;
  conditions?: string[];
  raw: ILibraryDetails | IMedicationLibraryDetails | ISurveyLibraryDetails;
};

export interface IFileType {
  md5Hash: string;
  method: string;
  signedUrl: string;
  exists?: boolean;
}

export interface IFileInfo {
  file: File | null;
  fileName: string;
  fileSize: string;
}

export interface ICarePlan {
  asset?: string;
  createdDate?: string | null;
  deletedDate?: string | null;
  effectiveDate?: string | null;
  groupIdentifier?: string;
  id?: string;
  notes?: null | INote[];
  patientId?: string;
  remoteId: string;
  source?: string;
  tags?: null | string[];
  tasks?: null | ITask[];
  timezone?: ICarePlanTimezone;
  title: string;
  updatedDate?: null | string;
  versions?: null | number;
}

export interface IDefaultCarePlan {
  enabled: boolean;
  remoteId: string;
  title: string;
}

export interface IDefaultCarePlans {
  [key: string]: IDefaultCarePlan;
}

export interface ICarePlanTimezone {
  abbreviation: string;
  identifier: string;
}

export interface INote {
  author: string;
  title: string;
  content: string;
  groupIdentifier: string;
  remoteId?: string;
  userInfo: Record<string, unknown> | null;
}

export interface IUserInfo {
  category: string;
  detailViewAsset?: string;
  detailViewCSS?: string;
  detailViewHTML?: string;
  detailViewImageLabel?: string;
  detailViewText?: string;
  detailViewURL?: string;
  image?: string;
  logText?: string;
  priority: string;
  subtitle?: string;
}

export interface IHealthKitLinkage {
  quantityIdentifier: IHealthKitLinkageQuantityIdentifierEnum;
  quantitytype: IHealthKitLinkageQuantityTypeEnum;
  unit: IHealthKitLinkageUnitEnum;
}

export enum IHealthKitLinkageQuantityIdentifierEnum {
  BLOOD_PRESSURE = 'bloodPressure',
  BLOOD_PRESSURE_DIASTOLIC = 'bloodPressureDiastolic',
  BLOOD_PRESSURE_SYSTOLIC = 'bloodPressureSystolic',
  BLOOD_GLUCOSE = 'bloodGlucose',
  BODY_FAT_PERCENTAGE = 'bodyFatPercentage',
  BODY_MASS = 'bodyMass',
  BODY_MASS_INDEX = 'bodyMassIndex',
  BODY_TEMPERATURE = 'bodyTemperature',
  ENVIRONMENTAL_AUDIO_EXPOSURE = 'environmentalAudioExposure',
  HEADPHONE_AUDIO_EXPOSURE = 'headphoneAudioExposure',
  HEART_RATE = 'heartRate',
  HEIGHT = 'height',
  LEAN_BODY_MASS = 'leanBodyMass',
  OXYGEN_SATURATION = 'oxygenSaturation',
  RESPIRATORY_RATE = 'respiratoryRate',
  RESTING_HEART_RATE = 'restingHeartRate',
  STEPS = 'steps',
  VO2MAX = 'vo2Max',
  WALKING_HEART_RATE_AVERAGE = 'walkingHeartRateAverage',
}

export enum IHealthKitLinkageQuantityTypeEnum {
  CUMULATIVE = 'cumulative',
  DISCRETE = 'discrete',
}

export enum HealthKitLinkageMethodEnum {
  ORAL = 'oral',
  INJECTION = 'injection',
}

export enum IHealthKitLinkageUnitEnum {
  BPM = 'bpm',
  COUNT = 'count',
  COUNT_MIN = 'count/min',
  LB = 'lb',
  L_MIN = 'L/min',
  MG_DL = 'mg/dl',
  ML_KL_MIN = 'ml/(kg*min)',
  MM_HG = 'mmHg',
  MG = 'mg',
  UNITS = 'Units',
  SEC = 'seconds',
  MIN = 'minutes',
  DAYS_WEEKS = 'days/week',
}

export enum ITaskScheduleTargetValueTypeEnum {
  BINARY = 'binary',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DOUBLE = 'double',
  INTEGER = 'integer',
  TEXT = 'text',
  UNITS = 'units',
}

export enum ITaskGroupIdentifierEnum {
  CHECKLIST = 'CHECKLIST',
  GRID = 'GRID',
  INSTRUCTION = 'INSTRUCTION',
  LABELED_VALUE = 'LABELED_VALUE',
  LOG = 'LOG',
  NUMERIC_PROGRESS = 'NUMERIC_PROGRESS',
  SIMPLE = 'SIMPLE',
  FEATURED_CONTENT = 'FEATURED_CONTENT',
  LINK = 'LINK',
  LIFESTYLE = 'LIFESTYLE',
  SYMPTOMS = 'SYMPTOMS',
  SYMPTOMS_SEVERITY = 'SYMPTOMS_SEVERITY',
  QUIZ = 'QUIZ',
  SURVEY = 'SURVEY',
  GOALS = 'GOALS',
  GOAL = 'GOAL',
  VIDEO = 'VIDEO',
  TEMPERATURE = 'TEMPERATURE',
  SMART_GOAL = 'SMART_GOAL',
  SCORE = 'SCORE',
  SCORE_BRANCHING = 'SCORE_BRANCHING',
}

export enum ILinkTaskTypeEnum {
  EMAIL = 'email',
  URL = 'url',
  APP_STORE = 'appStore',
  CALL = 'call',
  LOCATION = 'location',
  MESSAGE = 'message',
  WEBSITE = 'website',
}

export interface ILinkTask {
  title: string;
  type: ILinkTaskTypeEnum;
  latitude?: string;
  longitude?: string;
  appStore?: string;
  call?: string;
  email?: string;
  message?: string;
  url?: string;
  website?: string;
}

export interface ITask {
  asset: string;
  carePlanId: string;
  createdDate: null | string;
  deletedDate: null | string;
  effectiveDate: null | string;
  groupIdentifier?: ITaskGroupIdentifierEnum | TaskIdentifierEnum;
  healthKitLinkage: null | IHealthKitLinkage;
  hidden?: boolean;
  id?: string;
  impactsAdherence?: boolean;
  links?: ILinkTask[];
  instructions?: string;
  notes: null | ITaskNotes;
  remoteId?: string;
  schedules: ITaskSchedule[];
  source?: string;
  tags: null | string[];
  timezone?: ITimezone;
  title: string;
  updatedDate?: null | string;
  userInfo: IUserInfo;
  versions: null | number;
  isTouched?: boolean;
  startDateSetByUser?: boolean;
  imageFile?: File | undefined;
  pdfFile?: File | undefined;
  isEdited?: boolean;
  isDeleted?: boolean;
  imageUpdated?: boolean;
  offsetDays?: number;
  offsetTime?: Date;
}

export type ICategoryTasks = {
  title: string;
  tasks: ITask[];
};

export type ITaskMixpanel = {
  patientId: string;
  careplanId: string;
  action: string;
  taskId?: string;
};

export type IPatientsSearchMixpanel = {
  cohort: string;
  view: string;
  text: string;
  results: number;
  filters: string[];
};

export type ITaskNotes = {
  [key: string]: INote;
};

export interface ITaskSchedule {
  custom: boolean;
  daily: boolean;
  duration: number;
  end?: string | null; // Date time
  hour: number;
  interval?: number;
  minutes: number;
  start: string | null; // Date time
  targetValues: ITaskScheduleTargetValue[] | null;
  text: string;
  weekday?: number;
  weekly: boolean;
  startOffset?: number; // in seconds
}

export interface ITaskScheduleTargetValue {
  groupIdentifier?: string;
  index: number;
  kind: any;
  units?: IHealthKitLinkageUnitEnum | string;
  selectedUnits?: IDropdownOption;
  value: any;
  type: ITaskScheduleTargetValueTypeEnum;
}

export enum TaskIdentifierEnum {
  MEASUREMENT = 'measurements',
  MEDICATION = 'medications',
  SYMPTOMS = 'symptoms',
  SYMPTOMS_SEVERITY = 'symptoms_severity',
  LIFESTYLE = 'lifestyle',
  EDUCATION = 'education',
  LINKS = 'links',
  TYPEFORM = 'typeform',
  SURVEYS = 'surveys', // this is for the category
  TEMPERATURE = 'temperature',
  GOALS = 'goals',
}

export const taskCategoryFeatureFlagMap = {
  [TaskIdentifierEnum.MEASUREMENT]: 'careComposerMeasurementTasks',
  [TaskIdentifierEnum.MEDICATION]: 'careComposerMedicationTasks',
  [TaskIdentifierEnum.SYMPTOMS]: 'careComposerSymptomsTasks',
  [TaskIdentifierEnum.SURVEYS]: 'careComposerSurveysTasks',
  [TaskIdentifierEnum.LIFESTYLE]: 'careComposerLifestyleTasks',
  [TaskIdentifierEnum.EDUCATION]: 'careComposerEducationTasks',
  [TaskIdentifierEnum.LINKS]: 'careComposerLinksTasks',
  [TaskIdentifierEnum.GOALS]: 'careComposerSmartGoalsTasks',
} as const;

export interface ITaskFileType {
  taskId: string;
  file: File;
  careplanId?: string;
}

export interface ISurveysResponse {
  surveys: ISurvey[];
}

export interface IVideosResponse {
  videos: ISurvey[];
}

export interface ISurvey {
  id: string;
  title: string;
  url: string;
}

export interface ITaskFileNameType {
  taskId: string;
  fileName: string;
  carePlanId?: string;
}

export interface ITimezone {
  abbreviation: string;
  identifier: string;
}

export interface IDateRange {
  start: Date;
  end: Date | null;
}

export enum RpmBloodGlucoseInformationColorEnum {
  VERY_HIGH = 'VERY_HIGH',
  HIGH = 'HIGH',
  TARGET = 'TARGET',
  LOW = 'LOW',
  VERY_LOW = 'VERY_LOW',
}

export interface ICoords {
  top: number;
  left: number;
  bottom: number;
  right: number;
  elementsWidth: number;
  elementsHeight: number;
}

export enum InformationTooltipEnum {
  ACKNOWLEDGED = 'acknowledged',
  ACTIVE_COHORT = 'active_cohort',
  REFERRAL_COHORT = 'referral_cohort',
  ENGAGEMENT = 'engagement',
  ENROLLED = 'enrolled',
  ADHERENCE = 'adherence (7d)',
  ONLY_TEXT = 'only_text',
  BLOOD_GLUCOSE = 'blood_glucose',
  ADHERENCE_PERCENTAGE = 'adherence_percentage',
  ENGAGEMENT_COMPONENT = 'engagement_component',
  IMPACTS_ADHERENCE = 'impacts_adherence',
  NO_DATA = 'no_data',
  NO_MESSAGES = 'no_messages',
  CARE_PLAN_NAMES = 'care_plan_names',
  STICKY_NOTES = 'sticky_notes',
  CLINICIAN_RECEIPTS = 'clinician_receipts',
  TIMELINE_UPDATE = 'timeline_update',
  GLUCOSE_TIME_IN_ZONE = 'glucose_time_in_zone',
  GLUCOSE_TIME_IN_ZONE_4_WEEK = 'glucose_time_in_zone_4_week',
  BLOOD_PRESSURE_TIME_IN_ZONE = 'blood_pressure_time_in_zone',
  MASKED_TEXT = 'masked_text',
  BULK_MESSAGE = 'bulk_message',
  VITALS_WIDGET_TREND = 'vitals_widget_trend',
  BMI = 'bmi',
  CHARTS_TIME_IN_RANGE = 'charts_time_in_range',
  TIME_IN_RANGE_BUCKET = 'time_in_range_bucket',
  PATIENT_ASSISTANT_MODEL = 'patient_assistant_model',
  RECURRING_TASK = 'recurring_task',
  LIFESTYLE_NUMBERED = 'lifestyle_numbered',
}

export enum InformationTooltipDirectionEnum {
  TOP = 'top',
  RIGHT = 'right',
  LEFT = 'left',
  MIDDLE = 'middle',
  CENTER_MIDDLE = 'center_middle',
  TOP_CENTER_MIDDLE = 'top_center_middle',
}

export interface IInformationTooltip {
  id?: string;
  opened: boolean;
  direction: InformationTooltipDirectionEnum | string;
  adherenceValues?: IRPMAdherence;
  engagementValues?: IRPMEngagement;
  acknowledgment?: IRPMAlertAcknowledgement;
  acknowledgmentMeasurementColor?: RPMCohortViewColorsEnum;
  value?: number;
  timestamp?: string;
  text?: string;
  type: InformationTooltipEnum | string;
  coords: ICoords;
  title?: string;
  taskNames?: string[];
  notes?: INotesWidgetNote[];
  bmiWeight?: IPatientBMIMeasurement;
  bmiHeight?: IPatientBMIMeasurement;
  latestBloodPressureValue?: ILatestBloodPressureMeasurementValue;
  timelineUpdates?: ITimeline[];
  higherZindex?: boolean;
  clinicianReceipts?: ITwilioClinicianReceipt[];
  isBluetoothBillingMeasurements?: boolean;
  timelineEvent?: ITimelineEvent;
  billingInsightsMeasurements?:
    | IBillingInsightsPatientAssignment
    | IBillingInsightsPatientBluetoothMeasurement;
  weightChangePounds?: IWeightChangePoundsRangeTooltip;
  clinician?: string;
  source?: string;
  dateCreated?: string;
  dateUpdated?: string;
  completed?: boolean;
  messageModel?: string;
}

export interface IInformationTooltipRequest {
  event: Event;
  opened: boolean;
  direction: InformationTooltipDirectionEnum | string;
  type: InformationTooltipEnum | string;
  tooltipId?: string;
  title?: string;
  text?: string;
  color?: RPMCohortViewColorsEnum;
  adherenceValues?: IRPMAdherence;
  engagementValues?: IRPMEngagement;
  acknowledgment?: IRPMAlertAcknowledgement;
  value?: number;
  timestamp?: string;
  notes?: INotesWidgetNote[];
  bmiWeight?: IPatientBMIMeasurement;
  bmiHeight?: IPatientBMIMeasurement;
  latestBloodPressureValue?: ILatestBloodPressureMeasurementValue;
  weightChangePounds?: IWeightChangePoundsRangeTooltip;
}

export interface IGlucoseTimeInZoneTooltipRequest {
  event: Event;
  opened: boolean;
  tooltipId?: string;
  direction: InformationTooltipDirectionEnum | string;
  glucoseTimeInZone?: ITimeInZoneChartData;
}

export interface IGlucoseTimeInZoneTooltipPayload {
  glucoseTimeInZone?: ITimeInZoneChartData;
  coords: ICoords;
}

export interface IBPTimeInZoneTooltipRequest {
  event: Event;
  opened: boolean;
  tooltipId?: string;
  direction: InformationTooltipDirectionEnum | string;
  bloodPressureTimeInZone?: IBloodPressureTimeInZoneChartData;
}

export interface IBPInZoneTooltipPayload {
  bloodPressureTimeInZone?: IBloodPressureTimeInZoneChartData;
  coords: ICoords;
}

export interface IAcknowledgeAlertPayload {
  value: string | number;
  patientId: string;
  timestamp: string;
  color: RPMCohortViewColorsEnum;
  alert: IRPMAlert;
  firstLevelAttribute: AlertFirstLevelAttributeEnum;
  secondLevelAttribute?:
    | Exclude<keyof IRPMPatientBloodGlucose, 'units'>
    | 'systolic'
    | 'diastolic';
  engagement?: IRPMEngagement;
}

export interface IAcknowledgeAlertRequest {
  event: Event;
  direction?: InformationTooltipDirectionEnum | string;
  color: RPMCohortViewColorsEnum;
  value: number | string;
  alert: IRPMAlert;
  patientId: string;
  firstLevelAttribute: AlertFirstLevelAttributeEnum;
  secondLevelAttribute?:
    | Exclude<keyof IRPMPatientBloodGlucose, 'units'>
    | 'systolic'
    | 'diastolic';
  opened: boolean;
  newOpened: boolean;
  timestamp: string;
  engagement?: IRPMEngagement;
}

export interface IAcknowledgeTaskAlertPayload {
  patientId: string;
  carePlanId: string;
  value: string;
  taskId: string;
  alertId: string;
  silenced: boolean;
  firstLevelAttribute: AlertFirstLevelAttributeEnum;
  secondLevelAttribute:
    | Exclude<keyof IRPMPatientBloodGlucose, 'units'>
    | undefined
    | 'systolic'
    | 'diastolic';
}

export interface IAcknowledgeOutcomeAlertPayload extends IAcknowledgeTaskAlertPayload {
  outcomeId: string;
}

export interface IAcknowledgeAlertTooltip {
  newOpened: boolean;
  opened: boolean;
  direction: InformationTooltipDirectionEnum | string;
  payload: IAcknowledgeAlertPayload;
  coords: ICoords;
}

export interface IGlucoseTimeInZoneTooltip {
  opened: boolean;
  direction: InformationTooltipDirectionEnum | string;
  glucoseTimeInZone: ITimeInZoneChartData;
  coords: ICoords;
}

export interface IGlucoseTimeSeriesTooltip {
  opened: boolean;
  glucoseTimeSeries: IBloodGlucoseTimeSeriesMeasurement[];
  coords: ICoords;
}

export interface IGlucoseCGMTimeSeriesTooltip {
  opened: boolean;
  coords: ICoords;

  cgmHourlyMeasurement: IGlucoseCGMTimeSeriesHourPiece;
}

export interface IBloodPressureTimeSeriesTooltip {
  opened: boolean;
  bloodPressureTimeSeries: Array<
    IBloodPressureTimeSeriesData | IRpmBloodPressureMeasurement
  >;
  coords: ICoords;
}

export interface IDailyBloodPressureTooltip {
  opened: boolean;
  bloodPressure: IRpmBloodPressureMeasurement[];
  coords: ICoords;
}

export interface ICGMTimeSeriesTooltip {
  opened: boolean;
  cgmTimeSeries: IRpmContinuousGlucoseMeasurement[];
  coords: ICoords;
}

export interface IBPTimeInZoneTooltip {
  opened: boolean;
  direction: InformationTooltipDirectionEnum | string;
  bloodPressureTimeInZone: IBloodPressureTimeInZoneChartData;
  coords: ICoords;
}
export interface IWidgetMetricsTooltip {
  opened: boolean;
  title: string;
  zones: IPatientWidgetMetricZone[];
  coords: ICoords;
}

export interface ICalories2WeekDataTooltip {
  opened: boolean;
  caloriesMiniChartData: ICalories2WeekData | null;
  coords: ICoords;
  date: string | null;
  scrollThreshold?: number;
}

export interface ITriageAlertTooltipState {
  opened: boolean;
  payload?: Record<string, unknown> | null;
  coords?: ICoords;
  clickAway?: () => void;
}
export interface IRPMPaginationPayload {
  page: number;
  limit: number;
}

export interface IAuditPaginationPayload {
  page: number;
  limit: number;
  query: string;
  search: AuditCenterSearchByEnum;
}

export interface IMessageIndex {
  dateCreated: Date;
  index: number;
}

export interface ITwilioMessageStreamEvent {
  isLocal: boolean;
  message: ITwilioMessageStreamArg;
}

export interface ITwilioMessageStreamArg {
  sid: string;
  data: ITwilioMessageStreamData;
}

export interface ITwilioMessageStreamData {
  patientId?: string;
  cohortIds?: string[];
  userId?: string;
  name?: string;
  type?: ITwilioMessageStreamDataEnum;
}

export interface IMeasurementToastProps {
  name?: string;
  type: ITwilioMessageStreamDataEnum;
}

export interface IMeasurementToast {
  component: Component;
  props: IMeasurementToastProps;
}

export interface IMonthWithIndex {
  name: string;
  month: number;
  year: number;
}

export interface ISimpleTextToastProps {
  text: string;
  refresh: boolean;
}
export interface ISimpleTextToast {
  component: Component;
  props: ISimpleTextToastProps;
}

export enum ITwilioMessageStreamDataEnum {
  CAREPLAN_TASK_UPDATED = 'carePlanTaskUpdated',
  CAREPLAN_MEDICATION_UPDATED = 'carePlanMedicationUpdated',
  CAREPLAN_SYMPTOM_UPDATED = 'carePlanSymptomUpdated',
  CAREPLAN_SURVEY_UPDATED = 'carePlanSurveyUpdated',
  PATIENT_PROFILE = 'patientProfileUpdate',
  PATIENT_ENROLLED = 'patientEnrolled',
  PATIENT_ASSIGNED = 'patientCohortAssigned',
  PATIENT_DEREGISTERED = 'patientDeregistered',
  PATIENT_UNASSIGNED = 'patientCohortUnassigned',
  BLOODGLUCOSE_OUTCOME = 'bloodGlucoseOutcome',
  BLOODPRESSURE_OUTCOME = 'bloodPressureOutcome',
  INSULIN_OUTCOME = 'insulinOutcome',
  MEDICATION_OUTCOME = 'medicationOutcome',
  WEIGHT_OUTCOME = 'weightOutcome',
  CLINICIAN_ROLE_UPDATE = 'clinicianRoleUpdated',
  CLINICIAN_GROUP_UPDATE = 'clinicianGroupUpdated',
  CLINICIAN_DELETE = 'clinicianDeleted',
  ADHERENCE_UPDATED = 'patientAdherenceUpdated',
  IRREGULAR_HEART_RATE_OUTCOME = 'irregularHeartRhythmOutcome',
  HEART_RESTING_OUTCOME = 'restingHeartRateOutcome',
  STEPS_OUTCOME = 'stepsOutcome',
  SYMPTOM_OUTCOME = 'symptomOutcome',
  PATIENT_ENGAGEMENT = 'patientEngagement',
  TIMELINE_UPDATED = 'patientTimelineLog',
  TIMELINE_REVIEWED = 'patientTimelineReviewed',
  NOTE_UPDATE = 'patientNoteUpdate',
  SOAP_NOTE_UPDATE = 'patientSoapNoteUpdate',
  SOAP_NOTES_UPDATE = 'patientSOAPNotesUpdated',
  SURVEY_UPDATE = 'surveyOutcome',
  DEVICES_UPDATE = 'patientPeripheralDevices',
  PATIENT_CLOUD_INTEGRATIONS = 'patientCloudIntegrations',
  PATIENT_CARE_MODE_UPDATE = 'patientCareModeUpdate',
  FILTERS_UPDATE = 'cohortFilters',
  PATIENT_TAGS = 'patientCohortTags',
  CONVERSATION_UPDATE = 'patientConversationUpdated',
  APPOINTMENT_UPDATE = 'patientAppointmentUpdated',
  PATIENT_ASSISTANT_UPDATE = 'patientAssistantUpdated',
  SMART_GOAL_OUTCOME = 'smartGoalOutcome',
  LIFESTYLE_OUTCOME = 'lifestyleOutcome',
  TEMPERATURE_OUTCOME = 'temperatureOutcome',
}

export interface ITwilioConversation {
  dateCreated: Date;
  dateUpdated: Date;
  friendlyName: string;
  status: string;
  state: string;
  sid: string;
  uniqueName: string;
  lastMessage: IMessageIndex;
  lastReadMessageIndex: number | null;
  participants: ITwilioParticipant[];
  getParticipants: () => Promise<ITwilioParticipant[]>;
  getMessages: (
    pageSize: number,
    lastIndex?: number,
  ) => Promise<ITwilioMessagePaginator>;
  sendMessage: (
    message: string,
    attributes: ITwilioMessageAttributes,
  ) => Promise<number>;
  on: (event: string, callback: (param: any) => void | Promise<void>) => void;
  off: (event: string, callback: (param: any) => void | Promise<void>) => void;
  getUnreadMessagesCount: () => Promise<number>;
  getMessagesCount: () => Promise<number>;
  advanceLastReadMessageIndex: (lastIndex: number) => Promise<number>;
  getParticipantByIdentity: (identity: string) => Promise<ITwilioParticipant>;
  setAllMessagesRead: () => Promise<number>;
  prepareMessage: () => IMessageBuilder;
  leave: () => Promise<void>;
}

export interface IMessageBuilder {
  setBody(text: string): IMessageBuilder;
  addMedia(mediaOptions: IMediaOptions): IMessageBuilder;
  setAttributes(attributes: ITwilioMessageAttributes): IMessageBuilder;
  build(): IMessageBuilder;
  send(): Promise<void>;
}
export interface ITwilioMessageAttributes {
  bulkMessagePatients: number;
}

export interface ITwilioMessagesWithParticipants {
  messages: ITwilioMessage[];
  participants: IConversationParticipant[];
  patientIdentity?: string;
}

export interface ITwilioParticipant {
  dateCreated: Date;
  dateUpdated: Date;
  identity: string;
  lastReadMessageIndex: number | null;
  lastReadTimestamp: Date;
  friendlyName: string;
  conversation: ITwilioConversation;
  sid: string;
  on: (event: string, callback: (param: any) => void | Promise<void>) => void;
  off: (event: string, callback: (param: any) => void | Promise<void>) => void;
  getUser: () => Promise<ITwilioUser>;
}

export interface ITwilioParticipantUpdated {
  participant: ITwilioParticipant;
  updateReasons: string[];
}

export interface ITwilioPaginator {
  items: ITwilioConversation[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: () => Promise<ITwilioPaginator>;
}

export interface ITwilioMessagePaginator {
  items: ITwilioMessage[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  prevPage: () => Promise<ITwilioPaginator>;
}

export interface ITwilioMessage {
  conversation: ITwilioConversation;
  state: ITwilioMessageState;
  authorFriendlyName?: string;
  authorUniqueName?: string;
  authorRole?: string;
  myMessage?: boolean;
  type: string;
  media?: typeof Media;
  mediaURL?: string;
  fileName?: string;
  fileType?: string;
  sid: string;
  lastReadTimestamp?: string;
  lastReadClinicianTimestamps?: ITwilioClinicianReceipt[];
  videoDuration?: number;
}

export interface ITwilioClinicianReceipt {
  clinicianName: string;
  clinicianIdentity: string;
  lastReadTimestamp: string;
  lastReadTimestampDate: Date;
}

export interface ITwilioMessageState {
  author: string;
  body: string;
  dateUpdated: Date;
  index: number;
  participantSid: string;
  sid: string;
  timestamp: Date;
  type: string;
  attributes?: ITwilioMessageAttributes;
  medias?: (typeof Media)[];
}

export interface ITwilioSendMessage {
  channel: ITwilioConversation | undefined;
  message: string;
  mediaOptions?: IMediaOptions;
  bulkMessagePatients?: number;
  hasUnread?: boolean;
}

export interface IConversationParticipant {
  friendlyName: string;
  lastReadMessageIndex: number;
  lastReadTimestamp: string;
  title: string;
  userId: string;
}

export interface IConversationParticipants {
  participants: IConversationParticipant[];
}

export interface IConversationDraft {
  draft: string;
  dateCreated: string;
  dateUpdated: string;
}

export interface ITwilioMessagesWithParticipant {
  messages: ITwilioMessage[];
  participant: ITwilioParticipant;
  patientIdentity?: string;
}

export interface ITwilioMessagesWithConvParticipant {
  messages: ITwilioMessage[];
  participant: IConversationParticipant;
  patientIdentity?: string;
}

export interface ITwilioClient {
  connectionState: string;
  reachabilityEnabled: boolean;
  user: ITwilioUser;
  version: string;
  getSubscribedConversations: () => Promise<ITwilioPaginator>;
  getConversationByUniqueName: (
    identity: string,
  ) => Promise<ITwilioConversation>;
  getUser: (identity: string) => Promise<ITwilioUser>;
  updateToken: (token: string) => Promise<ITwilioClient>;
  on: (event: string, callback: (param: any) => void | Promise<void>) => void;
  off: (event: string, callback: (param: any) => void | Promise<void>) => void;
}

export interface ITwilioSyncClient {
  connectionState: string;
  on?: (event: string, callback: (param: any) => void | Promise<void>) => void;
  updateToken: (token: string) => Promise<ITwilioClient>;
  stream: (arg?: string) => Promise<SyncStream>;
}

export interface SyncStream {
  on: (event: string, callback: (param: any) => void | Promise<void>) => void;
}

export interface ITwilioUser {
  identity?: string;
  uniqueName: string;
  friendlyName: string;
  attributes?: any;
  isOnline?: boolean;
  lastMessageDate?: Date | string;
  date_created?: string;
  date_updated?: string;
  sid?: string;
  unreadMessages: boolean;
  unreadMessagesCount: number;
  hidden?: boolean;
  twilioUpdated?: boolean;
}

export interface ITwilioChannelAttributesPayload {
  channelUniqueName: string;
  unreadMessages?: boolean;
  lastMessageDate?: Date | string;
}

export interface IPaginationPayload {
  page: number;
  pageSize: number;
}

export interface ITwilioUsersAndConversations {
  users: ITwilioUser[];
  conversations: ITwilioUser[];
}

export interface ITwilioSearchConversationsPayload {
  conversations: ITwilioSearchConversations[];
}

export interface ITwilioSearchConversations {
  uniqueName: string;
  uniqueNameInternal: string;
  friendlyName: string;
}

export interface ITwilioSearchPatientLists {
  patientsToShow: ITwilioSearchConversations[];
  twilioPatientList: ITwilioUser[];
}

export interface IChartItemsTopSection {
  topLabel: string;
  bottomLabel: number;
  date: DateTime;
}

export interface IFeatureFlagsResponse {
  environmentClientId: string;
  user: LDUser;
}

export interface ISigmaURL {
  url: string;
}

export interface IIanaTimezone {
  aliasOf?: string | null;
  countries: string[];
  dstOffset: number;
  dstOffsetStr: string;
  name: string;
  utcOffset: number;
  utcOffsetStr: string;
}

export interface ILineChartDatum {
  x: number | Date;
  y: number;
  color: RPMCohortViewColorsEnum;
}

export interface ILineChartDatumWithoutColor {
  x: number | Date;
  y: number;
  displayedValue?: string;
}

export interface ILineChartDatumCircle {
  x: number | Date;
  y: number;
  value: number;
  displayedValue?: string;
}

export interface IBloodGlucoseLineChartInfo {
  chartData: ILineChartDatum[];
  path: Line<{
    x: number | Date;
    y: number;
    color: RPMCohortViewColorsEnum;
  } | null>;
}

export interface IHeartRestingLineChartInfo {
  chartData: ILineChartDatumWithoutColor[];
  path: Line<{ x: number | Date; y: number } | null>;
}

export interface IAreaChartDatum {
  x: number;
  y0: number;
  y1: number;
}

export interface IEducationalTaskImage {
  remoteId?: string;
  imageUrl?: string;
  carePlanId?: string;
  imageUpdated?: boolean;
}

export interface IPaginationMetadataItem {
  current: number;
  first: number;
  next?: number;
  previous?: number;
}

export interface IPaginationMetadata {
  page: IPaginationMetadataItem;
}

export interface IAxiosRequestCustomConfig extends AxiosRequestConfig {
  timestamp: number;
}

export interface ICountPerDay {
  [key: string]: ITimeline[];
}

export interface IFilterAttribute {
  count: number;
  displayName: string;
  from: string | null;
  to: string | null;
  value: string;
  isCustom?: boolean;
  valueSecondary: string | null;
}

export interface IFilter {
  attributes: IFilterAttribute[];
  displayName: string;
  fieldKey: string;
  filterType: FilterTypeEnum;
  numberType?: string;
  fieldType?: string;
  max?: string | number;
  min?: string | number;
  groups?: string[];
}

export interface ITagFilter extends IFilter {
  groupKey: string;
}

export interface IFilterTreeGroup {
  type: 'group';
  name: string;
  pathKey: string;
  depth: number;
  children: FilterTreeNode[];
}

export interface IFilterTreeLeaf {
  type: 'leaf';
  filter: IFilter;
  filterIndex: number;
  pathKey: string;
  depth: number;
}

export type FilterTreeNode = IFilterTreeGroup | IFilterTreeLeaf;

export interface IFilterVisibleGroupRow {
  type: 'group';
  depth: number;
  pathKey: string;
  name: string;
  expanded: boolean;
}

export interface IFilterVisibleLeafRow {
  type: 'leaf';
  depth: number;
  pathKey: string;
  filter: IFilter;
  filterIndex: number;
}

export type IFilterVisibleRow = IFilterVisibleGroupRow | IFilterVisibleLeafRow;

export interface ITagsFilter {
  attributes: IFilterAttribute[];
  displayName: string;
}

export interface ITagsWithCategory {
  name: string;
  category: string;
  color?: string;
}

export interface IAppliedFilterType {
  fieldKey: string;
  filterType: FilterTypeEnum;
  fieldType?: string;
  displayName?: string;
  appliedFilterAttribute: IFilterAttribute;
  isSelector?: boolean;
  hidden?: boolean;
  veiled?: boolean;
  unavailable?: boolean;
  index?: number;
}

export interface ILogbookTimeInterval {
  bloodGlucose: IRpmBloodGlucoseInformation[];
  fastActing: IRpmInsulinActing[];
  longActing: IRpmInsulinActing[];
}

export interface ILogbookTranslationBloodGlucoseDisplayInformation {
  breakfast: IRpmBloodGlucoseInformation[];
  lunch: IRpmBloodGlucoseInformation[];
  dinner: IRpmBloodGlucoseInformation[];
  bedTime: IRpmBloodGlucoseInformation[];
}

export interface ILogbookTranslationInsulinActingDisplayInformation {
  breakfast: IRpmInsulinActing[];
  lunch: IRpmInsulinActing[];
  dinner: IRpmInsulinActing[];
  bedTime: IRpmInsulinActing[];
}

export interface ILogbookMeasurement {
  breakfast: ILogbookTimeInterval;
  lunch: ILogbookTimeInterval;
  dinner: ILogbookTimeInterval;
  bedTime: ILogbookTimeInterval;
}

export interface ITimestampToIntervalDisplayInformation {
  timeInterval: keyof ILogbookMeasurement;
  timestamp: DateTime;
}

export interface ISummaryMenuTotalItem {
  total: number;
}

export interface ISummaryMenuTotals {
  patients: ISummaryMenuTotalItem;
  carePlans: ISummaryMenuTotalItem;
}

export interface IChartDates {
  startDate: DateTime;
  endDate: DateTime;
}

export interface IDragOptions {
  animation: number; // in ms
  group: string; // The class of draggable component
  disabled: boolean; // whether the draggable is false or true
  ghostClass: string; // the class in the behind element that is changing
}

export interface IMaxMinInfo {
  max: number;
  min: number;
}

export interface IDailyChartOpen {
  date: DateTime;
  chartType: DetailTabChartsEnum;
}

export interface IRPMOrganizationCarePlanPayload {
  nameHasChanged: boolean;
  carePlan: ICarePlan;
  clinicianTimezone?: string;
}

export interface ICohortViewDropdownOption {
  cohortId: string;
  cohortName: string;
  cohortView: ICohortViewInfoDropdownOption[];
}

export interface ICohortViewInfoDropdownOption {
  cohortId: string;
  cohortName: string;
  name: string;
  id: string;
  value: number;
}

export interface ISelectedSelector {
  selector: ISelectorView;
  selectorIndex: number;
}

export interface ISelectedSubSelector {
  subSelector: ISubSelector;
  subSelectorIndex: number;
}

export interface IGlucoseWidgetTimeSeries {
  bgm: IBloodGlucoseTimeSeriesMeasurement[];
  cgm: IGlucoseCGMTimeSeriesMeasurement[];
  thresholds: IRpmGlucoseThresholds;
}

export interface IGlucoseTimeSeries {
  bloodGlucose: IBloodGlucoseTimeSeriesMeasurement[];
  cgm: IGlucoseCGMTimeSeriesMeasurement[];
  thresholds: IRpmGlucoseThresholds;
}

export interface IBloodGlucoseTimeSeriesMeasurement {
  context: string;
  provenance: string;
  timestamp: string;
  value: number;
  color: string;
}

export interface IGlucoseCGMTimeSeriesMeasurement {
  color: string;
  provenance: string;
  timestamp: string;
  value: number;
}

export interface IGlucoseTimeSeriesDotData {
  hour: number;
  measurements: IBloodGlucoseTimeSeriesMeasurement[];
  colors: string[];
}

export interface IBloodPressureTimeSeries {
  bloodPressure: IBloodPressureTimeSeriesData[];
}

export interface IBloodPressureTimeSeriesDotData {
  hour: number;
  measurements: IBloodPressureTimeSeriesData[];
  displayInformation: IRpmBloodPressureDisplayInformation;
}

export interface ICalories2WeekData {
  calories: number;
  food: string[];
  timestamp: DateTime;
  scrollThreshold?: number;
}

export interface IGlucoseCGMTimeSeriesHourPiece {
  hour: number;
  minMeasurement: IGlucoseCGMTimeSeriesHourlyMeasurement;
  maxMeasurement: IGlucoseCGMTimeSeriesHourlyMeasurement;
}

export interface IGlucoseCGMTimeSeriesHourlyMeasurement {
  value: number;
  color: RPMCohortViewColorsEnum | string;
  timestamp: string;
}

export enum PreferredCommunicationMethodEnum {
  DEFAULT = '-',
  CALL = 'Call',
  MESSAGE = 'Message',
}

export enum VitalsWidgetBMITrendCategoryEnum {
  UNDERWEIGHT = 'UNDERWEIGHT',
  HEALTHY = 'HEALTHY',
  OVERWEIGHT = 'OVERWEIGHT',
  OBESE = 'OBESE',
  SEVERELY_OBESE = 'SEVERELY_OBESE',
}

export interface IFeet {
  feet: number;
  inches: number;
}

export enum VitalsWidgetWeightTrendCategoryEnum {
  SAME = 'SAME',
  INCREASE = 'INCREASE',
  LARGE_INCREASE = 'LARGE_INCREASE',
  DECREASE = 'DECREASE',
  LARGE_DECREASE = 'LARGE_DECREASE',
}

export enum TimeTrackerWidgetPurposeOfCallEnumm {
  IT_SUPPORT = 'IT Support',
  COACHING = 'Coaching / Education',
  CARE_TEAM_SUPPORT = 'Care Team Support',
}
